<script setup lang="ts">
import {useCdnUrl} from '@/composables/cdn-url';
import {Icon} from '@iconify/vue';
import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
import {Separator} from '@/lib/ui/separator';

const theHeader = ref<HTMLElement>();
const route = useRoute();
const router = useRouter();
const error = useError();
const menuActive = ref(false);

const {getUserProfile} = useUserStore();
const {userProfile} = storeToRefs(useUserStore());
const {isUserAuthorized} = storeToRefs(useAuthStore());

const {login, logout} = useAuthStore();

const localePath = useLocalePath();
const languagePickerActive = ref(false);

const {locale} = useI18n();

const {activate, deactivate} = useFocusTrap(theHeader);

watch(
  () => isUserAuthorized.value,
  () => {
    if (isUserAuthorized.value) getUserProfile();
  }
);

onMounted(() => {
  if (isUserAuthorized.value) getUserProfile();
});

const canLogin = computed(() => {
  const currentUrl = window.location.href;
  return currentUrl.includes('localhost') || currentUrl.includes('ots-dev') || currentUrl.includes('ots-stg');
});

function onLogin() {
  if (canLogin.value) {
    login();
  } else {
    return;
  }
}

function onLanguagePickerToggle() {
  languagePickerActive.value = !languagePickerActive.value;
  menuActive.value = languagePickerActive.value;
}

function onToggleMenu(evt: MouseEvent) {
  evt.stopPropagation();
  menuActive.value = !menuActive.value;
  if (menuActive.value) {
    activate();
    document.documentElement.style.overflow = 'hidden';
  } else {
    deactivate();
    document.documentElement.style.overflow = '';
    languagePickerActive.value = false;
  }
}

function onHomeClick(evt: Event) {
  evt.preventDefault();

  if (error) {
    clearError({redirect: '/'});
  }

  if ((route.name as string).includes('index')) {
    const element = document.getElementById('homeBanner');
    element!.scrollIntoView({block: 'start', behavior: 'smooth'});
  } else {
    router.push(localePath('/'));
  }
}
</script>

<template>
  <header ref="theHeader" class="the-header w-screen max-w-[2560px]">
    <div class="w-full flex flex-row justify-between items-center">
      <TheNuxtLink to="/" class="cursor-pointer" aria-label="Go to home page" @click="onHomeClick">
        <div class="ots-logo max-w-[200px] transition-width duration-300 ease-out h-[60px] w-[90px]">
          <nuxt-img :src="`${useCdnUrl()}/assets/logos/ots-logo-white.svg`" class="w-full drop-shadow-[1px_1px_2px_rgba(0,0,0,0.3)]" :alt="$t('one_times_square')" width="100%" height="auto" />
        </div>
      </TheNuxtLink>

      <nav class="top-nav flex gap-[3rem] items-center z-1001">
        <ul class="flex gap-[6rem] items-center">
          <li class="hide-mobile">
            <HeaderDropdownMenu @toggle-menu="onToggleMenu" />
          </li>
          <li class="hide-mobile">
            <TheNuxtLink to="/plan-your-visit" class="text-[1.8rem] font-semibold text-white hover:!underline">Plan Your Visit</TheNuxtLink>
          </li>
          <li class="hide-mobile">
            <TheNuxtLink to="/buy-tickets" class="text-[1.9rem] font-bold text-black uppercase px-[6rem] py-[1rem] rounded-full bg-white">Buy Tickets</TheNuxtLink>
          </li>
          <li>
            <Button class="cursor-pointer bg-black w-[5rem] h-[5rem] text-[1.75rem] font-bold uppercase" aria-label="Open language picker" @click="onLanguagePickerToggle">
              {{ locale }}
              <Icon icon="material-symbols:arrow-drop-down-rounded" class="!w-[3rem] !h-[3rem]" />
            </Button>
          </li>
          <li>
            <Button class="toggle-button rounded-full bg-black cursor-pointer !text-white w-[5rem] h-[5rem]" aria-label="Toggle main menu visibility" @click="onToggleMenu">
              <Icon v-if="!menuActive && !languagePickerActive" icon="solar:hamburger-menu-linear" />
              <Icon v-else icon="material-symbols-light:close" />
            </Button>
          </li>
        </ul>
      </nav>
    </div>

    <Transition name="fade">
      <div v-if="menuActive || languagePickerActive" id="mainMenu" class="sticky-menu" role="dialog" aria-labelledby="mainMenu_label" aria-modal="true" @click="onToggleMenu">
        <nav v-if="!languagePickerActive">
          <h2 id="mainMenu_label" class="sr-only">{{ $t('main_menu') }}</h2>

          <ul>
            <li>
              <span><NuxtLink to="https://www.icandynyc.com" target="_blank" @click="onToggleMenu">iCandy</NuxtLink></span>
            </li>

            <li>
              <span>
                <NuxtLink to="/nye-ball-crystal-vip-experience" @click="onToggleMenu">
                  NYE Ball and
                  <br />
                  Crystal VIP Experience
                </NuxtLink>
              </span>
            </li>
            <li>
              <span>
                <NuxtLink to="/#timesTravelFeature" @click="onToggleMenu">Times Travel + Viewing Deck</NuxtLink>
                <!-- {{ $t('sections.times_travel') }} -->
              </span>
            </li>
            <li>
              <span><NuxtLink to="/ever" @click="onToggleMenu">Ever-Weddings</NuxtLink></span>
            </li>

            <li><TheNuxtLink to="/buy-tickets" class="text-[1.9rem] font-bold text-black uppercase px-[6rem] py-[1rem] rounded-full bg-white">Buy Tickets</TheNuxtLink></li>
          </ul>

          <div class="w-[95px] ml-auto">
            <Separator class="inline-block" />
          </div>

          <ul class="medium-text">
            <li>
              <TheNuxtLink to="/plan-your-visit">Plan Your Visit</TheNuxtLink>
            </li>
            <li>
              <NuxtLink to="/bathrooms">Bathrooms</NuxtLink>
            </li>
            <li>
              <NuxtLink to="/faqs">{{ $t('footer.society.faqs') }}</NuxtLink>
            </li>
            <li v-if="!userProfile">
              <NuxtLink to="#" :class="{'cursor-not-allowed': !canLogin}" @click="onLogin">{{ $t('login') }}</NuxtLink>
            </li>
            <template v-else>
              <li>
                <NuxtLink :to="localePath('/profile')" class="text-[2rem] font-semibold text-white" @click="onToggleMenu">{{ $t('my_profile') }}</NuxtLink>
              </li>
              <li>
                <NuxtLink to="#" @click="logout">
                  {{ $t('logout') }}
                </NuxtLink>
              </li>
            </template>
            <li>
              <NuxtLink to="/">Home</NuxtLink>
            </li>
          </ul>
        </nav>

        <div v-else class="languages">
          <TheLanguagePicker :is-active="languagePickerActive" as-child @close="onToggleMenu" />
        </div>
      </div>
    </Transition>
  </header>
</template>

<style lang="scss">
@use '@/assets/scss/queries';

.the-header {
  background-color: black;
  left: 50%;
  padding: 1.5rem 8.5rem;
  position: fixed;
  top: 0;
  transform: translateX(-50%);
  width: 100%;
  z-index: 49;

  @include queries.for-size(laptop-small) {
    padding: 1.5rem 3.5rem;
  }

  @include queries.for-size(tablet-md) {
    padding: 1.75rem 3rem !important;

    .hide-mobile {
      display: none !important;
    }
  }

  .ots-logo {
    max-width: 200px;

    @include queries.for-size(phone) {
      max-height: 70px;
      max-width: 120px;
    }
  }

  .top-nav {
    @include queries.for-size(laptop-small) {
      ul {
        gap: 4rem;
      }
    }
  }

  .toggle-button {
    svg {
      height: 3rem;
      width: 3rem;
    }
  }

  .sticky-menu {
    align-items: center;
    align-items: start;
    background-color: rgba(0, 0, 0, 0.9);
    color: white;
    display: flex;
    font-size: 1.5rem;
    height: 100vh;
    justify-content: center;
    justify-content: end;
    left: 0;
    padding: 11rem 5rem;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1000;

    @include queries.for-size(phone) {
      padding: 10rem 3rem;
    }

    nav {
      display: flex;
      flex-direction: column;
      gap: 2rem;
      height: 100%;

      ul {
        align-items: flex-end;
        display: flex;
        flex-direction: column;
        gap: 2.5rem;
        justify-content: flex-end;

        li,
        li a {
          align-items: center;
          display: flex;
          font-weight: 700;
          gap: 2rem;
          text-align: right;

          &:hover {
            text-decoration: underline;
          }

          span {
            font-size: 2.6rem;
          }
        }

        &.medium-text {
          li {
            font-size: 1.8rem;
          }
        }

        &.small-text {
          li,
          li a {
            font-size: 2.75rem;
            gap: 0;

            @include queries.for-size(phone) {
              flex-direction: row;
              font-size: 2rem;
            }
          }
        }
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
